import {
  MARKETPLACE_CATEGORIES,
  type MarketplaceCategory,
  type MarketplaceEntryDto,
  type PluginDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  BadgeCheck,
  Check,
  CircleAlert,
  ExternalLink,
  Search,
  Server,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BrandMark } from '@/components/brand-mark';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Filter = MarketplaceCategory | 'all' | 'installed';

const RESOURCE_KEYS = ['plugins', 'skills', 'tools', 'mcp-servers', 'marketplace'];

interface InstallRequest {
  entry: MarketplaceEntryDto;
  parameters: Record<string, string>;
}

function matches(entry: MarketplaceEntryDto, query: string): boolean {
  if (!query) return true;
  const haystack = [
    entry.name,
    entry.publisher,
    entry.summary,
    entry.category,
    ...entry.tags,
    ...entry.contents.tools.map((t) => t.name),
    ...entry.contents.mcpServers.map((m) => m.name),
  ]
    .join(' ')
    .toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function PluginMark({ entry }: { entry: MarketplaceEntryDto }) {
  return <BrandMark name={entry.name} icon={entry.icon} brandColor={entry.brandColor} />;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function Contents({ entry }: { entry: MarketplaceEntryDto }) {
  const parts = [
    entry.contents.tools.length && plural(entry.contents.tools.length, 'tool'),
    entry.contents.skills.length && plural(entry.contents.skills.length, 'skill'),
    entry.contents.mcpServers.length && plural(entry.contents.mcpServers.length, 'MCP server'),
  ].filter(Boolean);
  return <span>{parts.join(' · ')}</span>;
}

function EnvStatus({ entry }: { entry: MarketplaceEntryDto }) {
  const required = entry.requiredEnv.filter((e) => !e.optional);
  if (required.length === 0) return <span className="text-muted-foreground">No setup needed</span>;
  const missing = required.filter((e) => !e.configured);
  return missing.length === 0 ? (
    <span className="inline-flex items-center gap-1">
      <Check className="size-3.5" aria-hidden /> Credentials set
    </span>
  ) : (
    <span className="inline-flex items-center gap-1">
      <CircleAlert className="size-3.5" aria-hidden /> Needs {missing.map((e) => e.key).join(', ')}
    </span>
  );
}

function DetailsDialog({
  entry,
  canEdit,
  installing,
  onInstall,
  onClose,
}: {
  entry: MarketplaceEntryDto | null;
  canEdit: boolean;
  installing: boolean;
  onInstall: (request: InstallRequest) => void;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const missing = entry?.parameters.some((p) => !values[p.key]?.trim()) ?? false;
  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open) {
          setValues({});
          onClose();
        }
      }}
    >
      {entry && (
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <PluginMark entry={entry} />
              <div className="grid gap-1">
                <DialogTitle className="flex items-center gap-2">
                  {entry.name}
                  {entry.verified && (
                    <BadgeCheck className="size-4" aria-label="Verified publisher" />
                  )}
                </DialogTitle>
                <DialogDescription>
                  {entry.publisher} · {entry.category} · v{entry.version}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="grid gap-4 text-sm">
            <p>{entry.description}</p>
            {entry.unavailable && (
              <p className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs">
                <CircleAlert className="size-3.5 shrink-0" aria-hidden />
                {entry.unavailable}. Ask your platform admin to allow it.
              </p>
            )}
            {!entry.installed && canEdit && entry.parameters.length > 0 && (
              <section className="grid gap-3">
                <h3 className="font-medium">Settings</h3>
                {entry.parameters.map((p) => (
                  <div key={p.key} className="grid gap-1.5">
                    <Label htmlFor={`param-${p.key}`}>{p.label}</Label>
                    <Input
                      id={`param-${p.key}`}
                      value={values[p.key] ?? ''}
                      placeholder={p.placeholder}
                      autoComplete="off"
                      spellCheck={false}
                      onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))}
                      aria-describedby={`param-${p.key}-hint`}
                    />
                    <p id={`param-${p.key}-hint`} className="text-muted-foreground text-xs">
                      {p.description}
                    </p>
                  </div>
                ))}
              </section>
            )}
            {entry.requiredEnv.length > 0 && (
              <section className="grid gap-2">
                <h3 className="font-medium">Environment variables</h3>
                <ul className="grid gap-1.5">
                  {entry.requiredEnv.map((e) => (
                    <li
                      key={e.key}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                    >
                      <div>
                        <code className="text-xs">{e.key}</code>
                        <p className="text-muted-foreground text-xs">
                          {e.description}
                          {e.secret && ' · store as a secret'}
                          {e.optional && ' · optional'}
                        </p>
                      </div>
                      {e.configured ? (
                        <Badge variant="secondary">
                          <Check aria-hidden /> Set
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not set</Badge>
                      )}
                    </li>
                  ))}
                </ul>
                {entry.requiredEnv.some((e) => !e.configured && !e.optional) && (
                  <p className="text-muted-foreground text-xs">
                    You can install now and add the variables later on the{' '}
                    <Link to="/environment" className="underline">
                      Environment
                    </Link>{' '}
                    page; calls fail until they are set.
                  </p>
                )}
              </section>
            )}
            {(['tools', 'skills', 'mcpServers'] as const).map((kind) =>
              entry.contents[kind].length > 0 ? (
                <section key={kind} className="grid gap-2">
                  <h3 className="flex items-center gap-1.5 font-medium">
                    {kind === 'tools' ? (
                      <Wrench className="size-4" aria-hidden />
                    ) : kind === 'skills' ? (
                      <Sparkles className="size-4" aria-hidden />
                    ) : (
                      <Server className="size-4" aria-hidden />
                    )}
                    {kind === 'tools' ? 'Tools' : kind === 'skills' ? 'Skills' : 'MCP servers'}
                  </h3>
                  <ul className="grid gap-1">
                    {entry.contents[kind].map((item) => (
                      <li key={item.name}>
                        <code className="text-xs">{item.name}</code>
                        {item.description && (
                          <span className="text-muted-foreground"> — {item.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null,
            )}
            {entry.homepage && (
              <a
                href={entry.homepage}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex w-fit items-center gap-1 underline"
              >
                Documentation <ExternalLink className="size-3.5" aria-hidden />
              </a>
            )}
          </div>
          <DialogFooter>
            {entry.installed ? (
              <Badge variant="secondary">
                <Check aria-hidden /> Installed {entry.installed.version}
              </Badge>
            ) : (
              canEdit && (
                <Button
                  disabled={installing || missing || entry.unavailable !== null}
                  onClick={() => onInstall({ entry, parameters: values })}
                >
                  {installing ? 'Installing…' : 'Install'}
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

/** Browse, search and install plugins from the built-in marketplace. */
export function PluginMarketplace({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<MarketplaceEntryDto | null>(null);
  const marketplace = useQuery({
    queryKey: ['marketplace', tenantId],
    queryFn: () => api<{ items: MarketplaceEntryDto[] }>('/plugins/marketplace'),
  });
  const install = useMutation({
    mutationFn: ({ entry, parameters }: InstallRequest) =>
      api<PluginDto>(`/plugins/marketplace/${encodeURIComponent(entry.id)}/install`, {
        method: 'POST',
        body: { parameters },
      }),
    onSuccess: (plugin, { entry }) => {
      toast.success(`${entry.name} installed`, {
        description: 'Attach its tools, skills or MCP server to an agent to use it.',
      });
      for (const key of RESOURCE_KEYS)
        void queryClient.invalidateQueries({ queryKey: [key, tenantId] });
      setSelected((current) =>
        current?.id === entry.id
          ? { ...current, installed: { id: plugin.id, version: plugin.version } }
          : current,
      );
    },
    onError: (error) => toast.error(error.message),
  });

  const entries = useMemo(() => marketplace.data?.items ?? [], [marketplace.data]);
  const visible = entries.filter(
    (e) =>
      matches(e, query) &&
      (filter === 'all' || (filter === 'installed' ? e.installed !== null : e.category === filter)),
  );
  const counts = (f: Filter) =>
    f === 'all'
      ? entries.length
      : f === 'installed'
        ? entries.filter((e) => e.installed).length
        : entries.filter((e) => e.category === f).length;
  const filters: Filter[] = [
    'all',
    ...MARKETPLACE_CATEGORIES.filter((c) => counts(c) > 0),
    'installed',
  ];

  return (
    <div className="grid gap-4">
      <div className="relative max-w-md">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search plugins, tools or publishers"
          aria-label="Search the marketplace"
          className="pl-8"
        />
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
        {filters.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            aria-pressed={filter === f}
            onClick={() => setFilter(f)}
            className="rounded-full"
          >
            {f === 'all' ? 'All' : f === 'installed' ? 'Installed' : f}
            <span className={cn('text-xs', filter === f ? 'opacity-80' : 'text-muted-foreground')}>
              {counts(f)}
            </span>
          </Button>
        ))}
      </div>
      <ErrorAlert error={marketplace.error} />
      {marketplace.isPending ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState title="No plugins match">Try another search or category.</EmptyState>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="Marketplace plugins">
          {visible.map((entry) => (
            <li key={entry.id}>
              <Card className="h-full" data-testid="marketplace-plugin">
                <CardHeader className="flex flex-row items-start gap-3 space-y-0">
                  <PluginMark entry={entry} />
                  <div className="grid min-w-0 gap-0.5">
                    <CardTitle className="flex items-center gap-1.5 text-base">
                      <span className="truncate">{entry.name}</span>
                      {entry.verified && (
                        <BadgeCheck className="size-4 shrink-0" aria-label="Verified publisher" />
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">{entry.publisher}</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm">
                  <p className="text-muted-foreground line-clamp-2">{entry.summary}</p>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline">{entry.category}</Badge>
                    {entry.tags.slice(0, 3).map((t) => (
                      <Badge key={t} variant="secondary" className="font-normal">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-muted-foreground grid gap-0.5 text-xs">
                    <Contents entry={entry} />
                    {entry.unavailable ? (
                      <span className="inline-flex items-center gap-1">
                        <CircleAlert className="size-3.5" aria-hidden /> Not available on this
                        server
                      </span>
                    ) : (
                      <EnvStatus entry={entry} />
                    )}
                  </div>
                </CardContent>
                <CardFooter className="mt-auto gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelected(entry)}
                    aria-label={`Details of ${entry.name}`}
                  >
                    Details
                  </Button>
                  {entry.installed ? (
                    <Badge variant="secondary" className="ml-auto">
                      <Check aria-hidden /> Installed
                    </Badge>
                  ) : (
                    canEdit && (
                      <Button
                        size="sm"
                        className="ml-auto"
                        disabled={
                          entry.unavailable !== null ||
                          (install.isPending && install.variables?.entry.id === entry.id)
                        }
                        onClick={() =>
                          // Entries with settings are installed from the details dialog.
                          entry.parameters.length > 0
                            ? setSelected(entry)
                            : install.mutate({ entry, parameters: {} })
                        }
                        aria-label={`Install ${entry.name}`}
                      >
                        {install.isPending && install.variables?.entry.id === entry.id
                          ? 'Installing…'
                          : entry.parameters.length > 0
                            ? 'Set up'
                            : 'Install'}
                      </Button>
                    )
                  )}
                </CardFooter>
              </Card>
            </li>
          ))}
        </ul>
      )}
      <DetailsDialog
        entry={selected}
        canEdit={canEdit}
        installing={install.isPending && install.variables?.entry.id === selected?.id}
        onInstall={(request) => install.mutate(request)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
